import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {inject} from '@angular/core';
import {Router} from '@angular/router';
import {catchError, switchMap, throwError} from 'rxjs';
import {AuthService} from './auth.service';

/**
 * True for an S3 address in either form (`bucket.s3.amazonaws.com`, `bucket.s3.us-east-1.amazonaws.com`, `s3.amazonaws.com`).
 * Signed S3 links carry their own credentials, so an Authorization header added here would make S3 refuse them.
 * Deliberately not "any amazonaws.com": the API itself is served from an `execute-api` host on that domain.
 */
export function isS3Url(url: string): boolean {
    return /^https?:\/\/([^\/]+\.)?s3([.-][a-z0-9-]+)?\.amazonaws\.com(\/|\?|$)/i.test(url);
}

export const customInterceptor: HttpInterceptorFn = (req, next) => {

    const loginEndpoint = '/auth/authenticate';
    const refreshTokenEndpoint = '/auth/refresh-token';
    const registerEndpoint = '/auth/register';
    const forgotPasswordEndpoint = '/auth/forgot-password';
    const resetPasswordEndpoint = '/auth/reset-password';

    const router = inject(Router);
    const authService = inject(AuthService);
// 1. Check if the URL is an S3 upload
    if (isS3Url(req.url)) {
        // Pass the request through EXACTLY as is, without adding any headers
        return next(req);
    }

    const anonymousEventEndpoint = '/events/public/';

    if (req.url.includes(loginEndpoint) || req.url.includes(refreshTokenEndpoint)
        || req.url.includes(registerEndpoint) || req.url.includes(forgotPasswordEndpoint)
        || req.url.includes(resetPasswordEndpoint) || req.url.includes(anonymousEventEndpoint)) {
        // Clone the request and add the custom header

        return next(req);
    }

    const accessToken = sessionStorage.getItem('access_token');

    if (accessToken !== null && accessToken !== undefined) {
        //   debugger;
        // Clone the request and add the custom header
        const reqWithHeaders = req.clone({
            setHeaders: {
                Authorization: `Bearer ${accessToken}`
            }
        });
        return next(reqWithHeaders).pipe(
            catchError((error: HttpErrorResponse) => {

                // -----------------------
                // ▶ 401 → user not logged in
                // -----------------------
                if (error.status === 401) {
                    router.navigate(['/login']);
                    return throwError(() => error);
                }

                // -----------------------
                // ▶ 403 → token expired
                // -----------------------
                const backendCode = error?.error?.code;
                const backendMessage = error?.error?.message;
                const isExpiredToken = backendCode === 'JWT_EXPIRED'
                    || backendMessage === 'JWT Token expired'
                    || error?.error?.access_denied_reason === 'JWT Token expired';

                if (error.status === 403 && isExpiredToken) {
                    console.warn("Token expired — requesting refresh...");

                    // Call refresh token API
                    return authService.refreshToken().pipe(
                        switchMap((newToken: any) => {

                            // ⬇ Save new access token
                            sessionStorage.setItem("access_token", newToken.accessToken);
                            sessionStorage.setItem("accessToken", newToken.accessToken);

                            // ⬇ Clone original request with new token
                            const retryRequest = req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${newToken.accessToken}`
                                }
                            });

                            // ⬇ Retry failed request
                            return next(retryRequest);
                        }),

                        catchError(refreshError => {
                            console.error("Refresh failed — redirecting to login");
                            sessionStorage.clear();
                            router.navigate(['/login']);
                            return throwError(() => refreshError);
                        })
                    );
                }

                console.error('Other error:', error.message);
                return throwError(() => error);
            })
        );

    } else {
        // If the access token is not available, proceed with the request without adding the header
        return next(req);
    }
};
